/* Volatile — full-screen search overlay (Figma 187:1121).
 *
 * Tap the header search icon → overlay slides in from the right, search
 * input autofocuses, and suggestions from the in-page catalogue snapshot populate
 * a 2-col (mobile) / 4-col (desktop) product grid below. Filters and Sort
 * open small bottom sheets; both apply to the cached result set client-
 * side so we never round-trip to a search page.
 *
 * Closing (back arrow / Esc / clear-x on empty input) restores the page
 * exactly where the user left it because the overlay is just a fixed
 * sibling — the underlying scroll position and component state are
 * never disturbed.
 */
(() => {
  const screen = document.querySelector('[data-search-screen]');
  const input = document.querySelector('[data-search-input]');
  const results = document.querySelector('[data-search-results]');
  const drawer = document.querySelector('[data-search-drawer]');
  if (!screen || !input || !results) return;

  // Products whose title starts with "test" are excluded from the search
  // dropdown. Mirrors the Liquid-side title-prefix filter in
  // `snippets/grid-cards.liquid` so the same products stay invisible across
  // collections, search results, and PDP recommendations while their direct
  // PDP URLs remain reachable. /search/suggest.json returns each product's
  // `title` so we can filter without an extra hydration step.
  const isTestProduct = (s) => ((s && s.title) || '').trim().toLowerCase().slice(0, 4) === 'test';

  /* ── Refine UI: single right-slide drawer with filter + sort tabs,
        used on every viewport. The mobile bottom sheets were removed —
        the floating refine FAB at the bottom-right of the overlay
        opens this drawer on every device. ─── */
  const openDrawer = (tab) => {
    if (!drawer || typeof drawer.showModal !== 'function') return;
    if (!drawer.open) drawer.showModal();
    if (tab) switchDrawerTab(tab);
  };
  const closeDrawer = () => {
    if (drawer && drawer.open) drawer.close();
  };
  const switchDrawerTab = (which) => {
    if (!drawer) return;
    drawer.querySelectorAll('[data-search-tab]').forEach((btn) => {
      const active = btn.dataset.searchTab === which;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    drawer.querySelectorAll('[data-search-panel]').forEach((panel) => {
      const active = panel.dataset.searchPanel === which;
      panel.classList.toggle('is-active', active);
      if (active) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  };

  const STATE_ATTR = 'data-search-open';

  /* ── Open / close ─────────────────────────────────────────────────── */
  // Stash the scrollY before pinning the body. Body becomes
  // `position: fixed` (via critical.css) which collapses scrollY to 0;
  // we offset its `top` so the page LOOKS pinned in place, then restore
  // the real scrollY on close. iOS Safari needs this to fully prevent
  // the page from scrolling underneath the overlay.
  let savedScrollY = 0;
  const open = () => {
    // Aliases are only needed once someone is actually searching, so the
    // file is pulled on first open rather than on every page load. TZ is
    // fully initialised by the time any user gesture can reach this.
    TZ.loadAliases();
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${savedScrollY}px`;
    document.body.setAttribute(STATE_ATTR, 'true');
    screen.setAttribute('aria-hidden', 'false');
    // Mobile browsers only open the virtual keyboard when focus happens
    // during the original tap gesture. Focus immediately for the keyboard,
    // then repeat after the slide starts in case layout steals focus.
    input.focus({ preventScroll: true });
    requestAnimationFrame(() => input.focus({ preventScroll: true }));
    setTimeout(() => input.focus({ preventScroll: true }), 120);
  };
  const close = () => {
    document.body.removeAttribute(STATE_ATTR);
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
    screen.setAttribute('aria-hidden', 'true');
    input.blur();
    closeDrawer();
  };

  /* ── Render helpers ───────────────────────────────────────────────── */
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  const moneyINR = (cents) => {
    const n = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })
      .format(Math.round(cents / 100));
    return n;
  };

  // Convert raw price (cents int OR rendered string) → "2,499" without the
  // currency prefix. We render INR / 2,499 / ¥ separately to match Figma.
  const priceNumOnly = (raw) => {
    if (raw == null) return '';
    if (typeof raw === 'number') return moneyINR(raw);
    let s = String(raw).replace(/<[^>]+>/g, '').trim();
    s = s.replace(/\.00\b/g, '')
         .replace(/^INR\s*/i, '')
         .replace(/^Rs\.?\s*/i, '')
         .replace(/^[₹$€£]\s*/, '');
    const n = Number(s.replace(/[^\d.]/g, ''));
    return (isFinite(n) && n > 0)
      ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })
          .format(Math.round(n))
      : s;
  };

  // Parse a price into cents (number) for client-side sorting / filtering.
  const priceToCents = (raw) => {
    if (raw == null) return 0;
    if (typeof raw === 'number') return Math.round(raw);
    let s = String(raw).replace(/<[^>]+>/g, '').trim()
      .replace(/^INR\s*/i, '').replace(/^Rs\.?\s*/i, '').replace(/^[₹$€£]\s*/, '');
    const n = Number(s.replace(/[^\d.]/g, ''));
    return isFinite(n) ? Math.round(n * 100) : 0;
  };

  // Approx INR → JPY converter (matches the v-price snippet's 1.85×2 path
  // — we don't have access to settings.jpy_rate from JS, so use a
  // reasonable default. Refine later if needed.)
  const yenFromInrCents = (cents) => {
    const inr = cents / 100;
    return Math.round(inr * 1.85 * 2);
  };

  /* ── Image URL helper — `/products/<handle>.js` returns image URLs
     as plain strings (no width param); `/search/suggest.json` returns
     them as objects with `.url`. Coerce to a plain URL string and
     append a Shopify-CDN width param for responsive loading. ─── */
  const imgUrl = (raw, width) => {
    if (!raw) return '';
    let u = typeof raw === 'string' ? raw : (raw.url || raw.src || '');
    if (!u) return '';
    // Shopify CDN URLs accept ?width=N — preserve any existing query.
    const sep = u.indexOf('?') === -1 ? '?' : '&';
    return `${u}${sep}width=${width}`;
  };

  /* ── Card render — the SAME multi-image markup as
     `snippets/product-card.liquid`. Used to be single-image only
     (suggest.json returns just `featured_image`); now we hydrate
     each result with `/products/<handle>.js` first so we have the
     full `images` array. Cards on the search overlay are now
     visually identical to the home Featured grid + PDP recs (same
     hover-stack swap, same mobile horizontal scroll-snap, same
     slider thumb + glass nav arrows). ── */
  const cardHTML = (p, index) => {
    const cents = priceToCents(p.price);
    const compareCents = priceToCents(p.compare_at_price);
    const onSale = compareCents > cents && cents > 0;
    const num = priceNumOnly(p.price);
    const yen = yenFromInrCents(cents);
    const compareNum = priceNumOnly(p.compare_at_price);

    // Normalize the image array. /products/<handle>.js returns plain URL
    // strings; /search/suggest.json returns featured_image as an object.
    const rawImages = Array.isArray(p.images) && p.images.length > 0
      ? p.images
      : (p.featured_image ? [p.featured_image] : []);
    const imageCount = rawImages.length;

    // Aspect ratio — prefer suggest.json's featured_image.aspect_ratio if
    // present, fall back to product-card default 0.8 (matches snippet).
    const ratio = (p.featured_image
                   && typeof p.featured_image === 'object'
                   && typeof p.featured_image.aspect_ratio === 'number'
                   && p.featured_image.aspect_ratio > 0)
      ? p.featured_image.aspect_ratio
      : 0.8;

    // image[0] is always primary, image[1] is always hover. The order
    // of product.images is governed by each product's front-first /
    // back-first tag and set by scripts/crisscross.py at media-upload
    // time. No per-card swap logic on the frontend.
    const primaryRaw = rawImages[0] || null;
    const hoverRaw   = rawImages[1] || primaryRaw;

    const primaryImg = primaryRaw
      ? `<img class="v-card__img v-card__img--primary" src="${esc(imgUrl(primaryRaw, 1200))}" alt="${esc(p.title)}" loading="lazy">`
      : '<div class="v-card__placeholder" aria-hidden="true"></div>';
    const hoverImg = (hoverRaw && hoverRaw !== primaryRaw)
      ? `<img class="v-card__img v-card__img--hover" src="${esc(imgUrl(hoverRaw, 1200))}" alt="${esc(p.title)}" loading="lazy">`
      : '';

    // Mobile scroll carousel — every product image, in the order the
    // product stores them.
    let scrollImgs = '';
    if (imageCount === 0) {
      scrollImgs = '<div class="v-card__placeholder" aria-hidden="true"></div>';
    } else {
      scrollImgs = rawImages.map((src) =>
        `<img class="v-card__scroll-img" src="${esc(imgUrl(src, 1200))}" alt="${esc(p.title)}" loading="lazy">`
      ).join('');
    }

    /* Intentionally out of stock (product tag out-of-stock): the card goes
       black and white with its tag, like snippets/product-card.liquid. */
    const oos = Array.isArray(p.tags) ? p.tags.includes('out-of-stock') : (typeof p.tags === 'string' && p.tags.split(',').map((t) => t.trim()).includes('out-of-stock'));
    const sizeOpt = Array.isArray(p.options) ? p.options.find((o) => o && typeof o.name === 'string' && o.name.trim().toLowerCase() === 'size') : null;
    const sizes = sizeOpt && Array.isArray(sizeOpt.values) ? sizeOpt.values.join(',') : '';
    const tag = oos
      ? `<span class="v-card__tag v-card__tag--notify" role="button" tabindex="-1" data-notify-open data-notify-handle="${esc(p.handle)}" data-notify-title="${esc(p.title)}" data-notify-sizes="${esc(sizes)}"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg><span>Notify me</span></span>`
      : onSale
        ? '<span class="v-card__tag">Sale</span>'
        : (p.available === false ? '<span class="v-card__tag v-card__tag--muted">Sold out</span>' : '');

    // Slider thumb + hover nav arrows — only when there's >1 image
    // (matches the snippet's `images.size > 1` gate).
    const slider = imageCount > 1
      ? `<div class="v-card__progress" data-card-dots data-image-count="${imageCount}" aria-hidden="true">
           <span class="v-card__progress-thumb"></span>
         </div>
         <span class="v-card__nav v-card__nav--prev" role="button" tabindex="-1" data-card-nav="prev" aria-hidden="true">
           <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 6 8 12l6 6"/></svg>
         </span>
         <span class="v-card__nav v-card__nav--next" role="button" tabindex="-1" data-card-nav="next" aria-hidden="true">
           <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 6l6 6-6 6"/></svg>
         </span>`
      : '';

    // Figure, struck compare-at, discount pill; no yen (Aditya, 2026-11-03),
    // mirroring snippets/v-price.liquid with show_off.
    const offPct = onSale ? Math.round((compareCents - cents) * 100 / compareCents) : 0;
    const priceInner = onSale
      ? `<s class="v-price__compare"><span class="v-price__cur">₹</span><span class="v-price__num">${esc(compareNum)}</span></s>` +
        `<span class="v-price__current"><span class="v-price__cur">₹</span><span class="v-price__num">${esc(num)}</span></span>` +
        `<span class="v-price__off" data-no-translate>-${offPct}%</span>`
      : `<span class="v-price__current"><span class="v-price__cur">₹</span><span class="v-price__num">${esc(num)}</span></span>`;

    return `
      <a class="v-card${(typeof index === 'number' && (index + 1) % 5 === 0) ? ' v-card--full-width' : ''}${oos ? ' v-card--oos' : ''}" href="${esc(p.url)}" aria-label="${esc(p.title)}">
        <div class="v-card__media" style="aspect-ratio: ${ratio};">
          <div class="v-card__hover-stack" aria-hidden="true">${primaryImg}${hoverImg}</div>
          <div class="v-card__scroll" data-card-scroll>${scrollImgs}</div>
          ${tag}
          ${slider}
        </div>
        <div class="v-card__meta">
          <span class="v-card__title">${esc(p.title)}</span>
          <span class="v-card__title-jp" lang="ja" data-jp-mirror>${esc(p.title)}</span>
          <span class="v-card__price"><span class="v-price">${priceInner}</span></span>
        </div>
      </a>`;
  };
  // Note: search-result cards keep `data-jp-mirror` (vs server-rendering)
  // because the predictive /search/suggest.json response doesn't include
  // product metafields, so we can't grab `custom.title_jp` here. translate.js
  // handles these the way it always did — runs the JP-mirror pass on
  // [data-jp-mirror] elements after results render.

  /* ── Suggestions ───────────────────────────────────────────────────
   *
   * This screen no longer shows a product grid. It is a single flat list of
   * suggestions, ordered category > collection > product (Aditya's order),
   * with no group headings and no counts - the row itself says what it is:
   *
   *   category    icon  + title + a grey CATEGORY tag
   *   collection  thumbnail + title
   *   product     title alone
   *
   * Everything is matched LOCALLY against the snapshot that
   * `snippets/search-overlay.liquid` prints into the page, products
   * included. That is an accuracy fix, not a speed one:
   * `/search/suggest.json` tokenises, so letters in the MIDDLE of a word
   * ("versized", "otor") matched nothing even though the product was
   * obviously there. Plain substring behaves the way a person expects.
   *
   * Because nothing else writes to the results container any more, this
   * owns it outright - no re-mounting, no fighting `renderGrid`.
   *
   * tenzen.in only.
   */
  const TZ = (() => {
    const on = window.location.hostname === 'tenzen.in';
    let meta = { types: [], collections: [], products: [] };
    const el = document.querySelector('[data-tz-search-meta]');
    if (el) { try { meta = JSON.parse(el.textContent); } catch (e) { /* leave defaults */ } }

    const typeSet = new Set((meta.types || []).map((t) => String(t).toLowerCase()));
    // A collection whose title IS a product type is a Category; the rest are
    // Collections. Same rule as the Browse screen and `is_drop`.
    // The page marks albums with the shared Liquid test (collection-is-drop);
    // title-equals-type is only the fallback for a page cached before that.
    const isCategory = (c) => (typeof c.album === 'boolean' ? !c.album : typeSet.has(String(c.t).toLowerCase()));
    const cols = meta.collections || [];
    const prods = meta.products || [];

    /* ── Aliases ────────────────────────────────────────────────────
       `assets/search-aliases.json` maps what people actually type ("ts",
       "tsh", "joggers", "amc") onto the real category and album titles.
       It is fetched once, the first time the overlay opens, and editing
       that file is the entire workflow - no code change.

       An alias hits when it starts with what has been typed (2 characters
       minimum, so "t" does not fire everything), or when what has been
       typed starts with the alias. Everything degrades to plain substring
       matching if the file never loads. */
    let aliasMap = null;          // { lowercased title -> [alias, ...] }
    let aliasLoading = false;

    // Product thumbnails are fetched once, the first time the overlay opens,
    // so they are cached before the first keystroke paints a row (about 2 KB
    // each).
    let thumbsWarm = false;
    const warmThumbs = () => {
      if (thumbsWarm || !on) return;
      thumbsWarm = true;
      prods.forEach((p) => { if (p.img) { const i = new Image(); i.decoding = 'async'; i.src = p.img; } });
    };

    const loadAliases = () => {
      warmThumbs();
      if (aliasMap || aliasLoading) return;
      const src = el && el.getAttribute('data-aliases');
      if (!src) { aliasMap = {}; return; }
      aliasLoading = true;
      fetch(src, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const out = {};
          const raw = (d && d.aliases) || {};
          Object.keys(raw).forEach((k) => {
            out[k.toLowerCase()] = (raw[k] || []).map((a) => String(a).toLowerCase());
          });
          aliasMap = out;
          // Whatever is already typed should benefit immediately.
          if (lastQuery) paint(lastQuery);
        })
        .catch(() => { aliasMap = {}; })
        .finally(() => { aliasLoading = false; });
    };

    const aliasHit = (title, q) => {
      if (!aliasMap || q.length < 2) return false;
      const list = aliasMap[String(title).toLowerCase()];
      if (!list) return false;
      return list.some((a) => a.startsWith(q) || q.startsWith(a));
    };

    /* Aliases of 3+ characters are also tested against product titles, which
       is how "joggers" reaches the sweatpant and "tsh" reaches every tee.
       Two-character aliases are skipped here on purpose: "ts" against a
       product title would match far too much. */
    const aliasTerms = (q) => {
      if (!aliasMap || q.length < 2) return [];
      const terms = [];
      Object.keys(aliasMap).forEach((title) => {
        const list = aliasMap[title];
        if (!list.some((a) => a.startsWith(q) || q.startsWith(a))) return;
        list.forEach((a) => { if (a.length >= 3) terms.push(a); });
      });
      return terms;
    };

    const hit = (title, q) => String(title).toLowerCase().includes(q);

    const CHEV =
      '<svg class="tz-s__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
      ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="m9 5 7 7-7 7"/></svg>';

    const rowHTML = (item, kind) => {
      let media = '';
      if (kind === 'category') {
        media = item.icon
          ? `<span class="tz-s__media tz-s__media--icon"><img src="${esc(item.icon)}" alt="" width="30" height="30" loading="lazy"></span>`
          : '<span class="tz-s__media tz-s__media--icon"></span>';
      } else if (kind === 'collection') {
        media = item.img
          ? `<span class="tz-s__media tz-s__media--thumb"><img src="${esc(item.img)}" alt="" width="44" height="44" loading="lazy"></span>`
          : '<span class="tz-s__media tz-s__media--thumb"></span>';
      } else if (kind === 'product') {
        // Already in the browser cache (warmThumbs), so no lazy loading.
        media = item.img
          ? `<span class="tz-s__media tz-s__media--thumb"><img src="${esc(item.img)}" alt="" width="32" height="32" decoding="async"></span>`
          : '<span class="tz-s__media tz-s__media--thumb"></span>';
      }
      // Both kinds carry a caption; only a product row goes without one.
      const label = kind === 'category' ? 'Category' : (kind === 'collection' ? 'Album' : '');
      const tag = label ? `<span class="tz-s__tag">${label}</span>` : '';
      return `<a class="tz-s__row tz-s__row--${kind}" href="${esc(item.u)}">` +
               media +
               `<span class="tz-s__text"><span class="tz-s__label">${esc(item.t)}</span>${tag}</span>` +
               CHEV +
             `</a>`;
    };

    const paint = (raw) => {
      if (!on || !results) return;
      const q = String(raw || '').trim().toLowerCase();

      if (!q) {
        results.innerHTML = '<p class="v-search-screen__hint">Start typing to search.</p>';
        return;
      }

      const terms = aliasTerms(q);
      const hitAny = (title) => hit(title, q) || terms.some((t) => hit(title, t));

      /* Alias intent outranks a coincidental substring. Typing "ts" hits
         Tees by alias, but also matches "Sweatpan[ts]" literally - without
         this the coincidence sorts above the thing that was meant, because
         the snapshot is alphabetical. A title that starts with the query
         ranks above one that merely contains it, for the same reason. */
    const rank = (c) => {
        if (aliasHit(c.t, q)) return 0;
        if (String(c.t).toLowerCase().startsWith(q)) return 1;
        return 2;
      };
      const pick = (wantCategory) => cols
        .filter((c) => isCategory(c) === wantCategory && (hit(c.t, q) || aliasHit(c.t, q)))
        .sort((a, b) => rank(a) - rank(b))
        .slice(0, 5);

      const cats = pick(true);
      const drops = pick(false);
      const items = prods.filter((pr) => hitAny(pr.t)).slice(0, 12);

      // No Shop All row: it is neither a category nor an album, so it has no
      // business in the suggestions.
      const html =
        cats.map((c) => rowHTML(c, 'category')).join('') +
        drops.map((c) => rowHTML(c, 'collection')).join('') +
        items.map((pr) => rowHTML(pr, 'product')).join('');

      results.innerHTML = html
        ? `<div class="tz-s">${html}</div>`
        : `<p class="v-search-screen__empty">No results for &ldquo;${esc(raw)}&rdquo;.</p>`;
    };

    return { on, paint, loadAliases };
  })();

  const renderHint = (text) => {
    results.innerHTML = `<p class="v-search-screen__hint">${esc(text)}</p>`;
  };
  const renderEmpty = (q) => {
    results.innerHTML = `<p class="v-search-screen__empty">No results for &ldquo;${esc(q)}&rdquo;.</p>`;
  };
  /* This screen has no product grid any more. Every path that used to end
     here - the local index warm-up, the filter checkboxes, the sort radios,
     resetRefine - now repaints the suggestion list instead, so none of them
     can put a grid back on screen. Kept as a funnel rather than deleted,
     because those call sites belong to the refine drawer, which is shared. */
  const renderGrid = () => { TZ.paint(lastQuery); };

  /* ── Filter / sort state ──────────────────────────────────────────── */
  let cachedProducts = [];
  let activeFilters = new Set();
  let activeSort = 'relevance';

  const applyFiltersAndSort = () => {
    let list = cachedProducts.slice();
    if (activeFilters.has('available')) {
      list = list.filter((p) => p.available !== false);
    }
    if (activeFilters.has('sale')) {
      list = list.filter((p) => priceToCents(p.compare_at_price) > priceToCents(p.price));
    }
    switch (activeSort) {
      case 'price-asc':  list.sort((a, b) => priceToCents(a.price) - priceToCents(b.price)); break;
      case 'price-desc': list.sort((a, b) => priceToCents(b.price) - priceToCents(a.price)); break;
      case 'title-asc':  list.sort((a, b) => String(a.title).localeCompare(String(b.title))); break;
      // 'relevance' = whatever order suggest.json returned (default)
      default: break;
    }
    renderGrid(list);
  };

  /* ── Live search ──────────────────────────────────────────────────── */
  let lastQuery = '';
  let suggestController;
  let productIndexReady = false;
  let productIndexPromise = null;
  let productIndex = [];
  // Cache hydrated products by handle so repeat queries don't refetch.
  const productCache = new Map();

  const normalizeProduct = (p) => {
    const firstVariant = (Array.isArray(p.variants) && p.variants[0]) || {};
    const images = Array.isArray(p.images) ? p.images : [];
    const firstImage = images[0] || p.featured_image || null;
    const ratio = firstImage && firstImage.width && firstImage.height
      ? firstImage.width / firstImage.height
      : undefined;
    const available = Array.isArray(p.variants)
      ? p.variants.some((v) => v.available !== false)
      : p.available;
    const normalized = {
      ...p,
      url: p.url || `/products/${p.handle}`,
      price: p.price || firstVariant.price,
      /* The compare-at field now carries the bag's MRP (2.5x the price), not
         a sale price, so it says nothing a search card should repeat. */
      compare_at_price: null,
      available,
      featured_image: firstImage
        ? { ...(typeof firstImage === 'object' ? firstImage : { url: firstImage }), aspect_ratio: ratio || 0.8 }
        : p.featured_image,
      images,
      _searchText: `${p.title || ''} ${p.handle || ''} ${p.product_type || ''} ${(p.tags || []).join(' ')}`
        .toLowerCase()
        .replace(/[-_]+/g, ' '),
    };
    if (normalized.handle) productCache.set(normalized.handle, normalized);
    return normalized;
  };

  const loadProductIndex = async () => {
    if (productIndexPromise) return productIndexPromise;
    productIndexPromise = fetch('/products.json?limit=250', { headers: { 'Accept': 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        productIndex = ((data && data.products) || [])
          .map(normalizeProduct)
          .filter((p) => !isTestProduct(p));
        productIndexReady = productIndex.length > 0;
        return productIndex;
      })
      .catch(() => {
        productIndexReady = false;
        productIndex = [];
        return productIndex;
      });
    return productIndexPromise;
  };

  const localMatches = (q) => {
    const normalized = q.trim().toLowerCase().replace(/[-_]+/g, ' ');
    if (!normalized) return [];
    const terms = normalized.split(/\s+/).filter(Boolean);
    return productIndex
      .map((p, index) => {
        const title = String(p.title || '').toLowerCase();
        const haystack = p._searchText || title;
        if (!terms.every((term) => haystack.includes(term))) return null;
        let score = 0;
        if (title === normalized) score += 100;
        if (title.startsWith(normalized)) score += 60;
        if (p.handle && p.handle.toLowerCase().startsWith(normalized.replace(/\s+/g, '-'))) score += 30;
        score += Math.max(0, 20 - index);
        return { p, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map((hit) => hit.p);
  };

  const renderLocalResults = (q) => {
    const matches = localMatches(q);
    cachedProducts = matches;
    if (matches.length) applyFiltersAndSort();
    else renderEmpty(q);
  };

  /* Was a `/products.json?limit=250` prefetch to power the local grid. The
     in-page snapshot supersedes it, so the overlay no longer pulls a
     quarter-megabyte of catalogue on open. */
  const warmSearchIndex = () => {};

  if ('requestIdleCallback' in window) {
    requestIdleCallback(warmSearchIndex, { timeout: 1800 });
  } else {
    setTimeout(warmSearchIndex, 600);
  }

  // Pull the full product (all images, variants) from `/products/<handle>.js`
  // so the search overlay can render the EXACT same card markup the home
  // Featured grid + PDP recs render. Single-image products skip the fetch
  // since the suggest.json result already has everything needed.
  const hydrate = async (stub) => {
    if (!stub.handle) return stub;
    if (productCache.has(stub.handle)) return productCache.get(stub.handle);
    try {
      const r = await fetch(`/products/${stub.handle}.js`,
        { headers: { 'Accept': 'application/json' } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const full = await r.json();
      // Merge: keep suggest.json's featured_image (object form with
      // aspect_ratio) but adopt full.images (array of all URLs).
      const merged = {
        ...stub,
        ...full,
        // suggest.json has a richer featured_image object (with
        // aspect_ratio + alt) — preserve it.
        featured_image: stub.featured_image || full.featured_image,
        // /products/<handle>.js gives us all images.
        images: Array.isArray(full.images) && full.images.length > 0
          ? full.images
          : (stub.featured_image ? [stub.featured_image] : []),
        // Preserve the suggest.json url (clean PDP URL).
        url: stub.url || full.url || `/products/${stub.handle}`,
      };
      productCache.set(stub.handle, merged);
      return merged;
    } catch (err) {
      productCache.set(stub.handle, stub);
      return stub;
    }
  };

  /* Everything this screen shows now comes from the in-page snapshot, so
     there is no request to make, nothing to abort, and no stale-response
     race to guard against. The debounce upstream still applies. */
  const fetchAndRender = async (q) => {
    lastQuery = q.trim();
    if (suggestController) suggestController.abort();
    cachedProducts = [];
    TZ.paint(lastQuery);
  };

  input.addEventListener('input', () => {
    const v = input.value;
    fetchAndRender(v);
  });

  // Pressing Enter on the input still navigates to the full search page
  // (form's default action) for accessibility — users who prefer the
  // dedicated search results page get it. The overlay close happens via
  // the submit-then-navigate, no JS preventDefault needed.

  /* ── Click / key handling for open / close / sheets ───────────────── */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-search]')) {
      e.preventDefault();
      open();
      return;
    }
    if (e.target.closest('[data-close-search]')) {
      e.preventDefault();
      close();
      return;
    }
    if (e.target.closest('[data-search-clear]')) {
      e.preventDefault();
      if (input.value.length > 0) {
        input.value = '';
        cachedProducts = [];
        TZ.paint('');
        input.focus();
      } else {
        close();
      }
      return;
    }
    if (e.target.closest('[data-search-refine]')) {
      e.preventDefault();
      // Refine FAB — open the side drawer on the filter tab by default.
      openDrawer('filter');
      return;
    }
    const tabBtn = e.target.closest('[data-search-tab]');
    if (tabBtn) {
      e.preventDefault();
      switchDrawerTab(tabBtn.dataset.searchTab);
      return;
    }
    if (e.target.closest('[data-close-search-drawer]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (e.target.closest('[data-search-drawer-clear]')) {
      e.preventDefault();
      resetRefine();
      return;
    }
  });

  /* Reset every filter / sort input across both the desktop drawer and the
     two mobile sheets so the visible UI matches the cleared state. */
  const resetRefine = () => {
    document.querySelectorAll('[data-search-filter-input]').forEach((cb) => { cb.checked = false; });
    document.querySelectorAll('[data-search-sort-input]').forEach((rb) => { rb.checked = rb.value === 'relevance'; });
    activeFilters = new Set();
    activeSort = 'relevance';
    applyFiltersAndSort();
  };

  // Filter checkboxes — toggle activeFilters, re-render.
  document.querySelectorAll('[data-search-filter-input]').forEach((cb) => {
    cb.addEventListener('change', () => {
      const v = cb.value;
      if (cb.checked) activeFilters.add(v);
      else activeFilters.delete(v);
      applyFiltersAndSort();
    });
  });
  // Sort radios — set activeSort, re-render.
  document.querySelectorAll('[data-search-sort-input]').forEach((rb) => {
    rb.addEventListener('change', () => {
      if (rb.checked) {
        activeSort = rb.value;
        applyFiltersAndSort();
      }
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (drawer && drawer.open) { drawer.close(); return; }
    if (document.body.getAttribute(STATE_ATTR) === 'true') close();
  });

  // Backdrop tap closes the side drawer.
  if (drawer) {
    drawer.addEventListener('click', (e) => { if (e.target === drawer) drawer.close(); });
  }
})();
